import { frontOverhangViolations } from "@CommonUtils/pog/frontOverhang";
import { frontOverhangViolationText } from "../common/frontOverhangViolationText";
import { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { PrimitiveAtom, useAtom, useSetAtom } from "jotai";

import { Pog } from "@CommonTypes/merchflow/pog/pog";
import {
  PlanogramItem,
  type PlanogramItemCommonNonCircular,
  PlanogramItemOrientation,
  UnrangedPlanogramItem,
} from "@CommonTypes/merchflow/pog/item";
import { adapterPlanogramItemToPlanogramItemCommon } from "@CommonAdapters/pog";
import { useToasts } from "src/components/Toasts";
import { useTranslation } from "src/i18n";
import {
  PlanogramContainer,
  UnrangedPlanogramContainer,
} from "@CommonTypes/merchflow/pog/container";
import { PlanogramBay } from "@CommonTypes/merchflow/pog/bay";
import type { BaseProduct } from "@CommonTypes/merchflow/pog/product";
import { uuid } from "@CommonUtils/uuid";
import {
  hasObstructionSpans,
  resizeShelfDeadSpans,
  resizeShelfWidth,
} from "@CommonUtils/pog/shelfMerchandising";
import { canSetPlanogramItemOrientation } from "../components/Panel/panels/components/planogramOrientation";
import { recalculateCdtPriorities } from "../components/Panel/panels/CdtPriorityOrder/store/utils";
import {
  areShelvesInSameMergedGroup,
  findNewStacksOverClearance,
  resolveDraggedItems,
  resolveDraggedPlanogramItems,
  standsOnWithinBay,
  type StackOverClearance,
} from "./utils";
import { useCanDeclareAisleBreaks } from "./aisleBreakEditing";
import { cdtSnakingInferenceRequestAtom } from "./cdtPlanogramEditorSessionAtoms";
import { getTraversalRows, type SnakingPattern } from "../common/snakingInference";

/**
 * The default notch for a NEW shelf: the first one whose deck stands at
 * or above the base (yBottom >= 0). Notch 1 can resolve below the deck on
 * real fixtures — a negative notchOffset puts yBottom(1) under y=0 — so a
 * defaulted shelf was born out of sight at y=-300. Inverse of the shelf's
 * own getYBottom (yBottom = notchOffset + (n-1)·spacing + offset −
 * thickness, offset 0 for a new shelf), the same inversion getNewNotchNo
 * uses; clamped to the bay's notch range.
 */
export const firstNotchOnDeck = (
  bay: { maxNotch: number; notchOffset: number; notchSpacing: number },
  thickness: number,
): number =>
  bay.notchSpacing > 0
    ? Math.max(
        1,
        Math.min(bay.maxNotch, Math.ceil((thickness - bay.notchOffset) / bay.notchSpacing + 1)),
      )
    : 1;

export const flipShelvesHorizontallyInPog = ({
  pog,
  shelfIds,
}: {
  pog: Pog;
  shelfIds: string[];
}) => {
  if (shelfIds.length === 0) {
    return null;
  }

  const newPog = new Pog(pog.toNonCircular());
  const shelfGroups = new Map<string, PlanogramShelf>();

  for (const shelfId of new Set(shelfIds)) {
    const shelf = newPog.getShelf(shelfId);
    if (!shelf) {
      return null;
    }

    const firstShelf = shelf.getFirstShelf();
    shelfGroups.set(firstShelf.uniqueId, firstShelf);
  }

  for (const shelf of shelfGroups.values()) {
    const mergedShelves = shelf.getMergedShelves();
    const reversedItems = mergedShelves.flatMap((mergedShelf) => mergedShelf.items).reverse();
    let itemIndex = 0;

    for (const mergedShelf of mergedShelves) {
      const itemCount = mergedShelf.items.length;
      mergedShelf.items = reversedItems.slice(itemIndex, itemIndex + itemCount);
      itemIndex += itemCount;

      for (const item of mergedShelf.items) {
        item.shelf = mergedShelf;
      }
    }
  }

  return newPog;
};

export const flipShelfHorizontallyInPog = ({ pog, shelfId }: { pog: Pog; shelfId: string }) =>
  flipShelvesHorizontallyInPog({ pog, shelfIds: [shelfId] });

export const flipItemsHorizontallyInPog = ({ pog, itemIds }: { pog: Pog; itemIds: string[] }) => {
  const newPog = new Pog(pog.toNonCircular());
  const selectedItemIds = new Set(itemIds);
  const slots: { shelf: PlanogramShelf; index: number }[] = [];

  findSelectedItems: for (const bay of newPog.planogram.bays) {
    for (const shelf of bay.shelves) {
      for (let index = 0; index < shelf.items.length; index++) {
        if (selectedItemIds.has(shelf.items[index].uniqueId)) {
          slots.push({ shelf, index });

          if (slots.length === selectedItemIds.size) {
            break findSelectedItems;
          }
        }
      }
    }
  }

  if (
    selectedItemIds.size === 0 ||
    slots.length !== selectedItemIds.size ||
    !areShelvesInSameMergedGroup(slots.map((slot) => slot.shelf))
  ) {
    return null;
  }

  const reversedItems = slots.map(({ shelf, index }) => shelf.items[index]).reverse();

  for (let index = 0; index < slots.length; index++) {
    const slot = slots[index];
    const item = reversedItems[index];
    slot.shelf.items[slot.index] = item;
    item.shelf = slot.shelf;
  }

  return newPog;
};

export const applySnakingInPog = ({ pog, to }: { pog: Pog; to: "odd" | "even" }) => {
  const newPog = new Pog(pog.toNonCircular());
  const rows = getTraversalRows<BaseProduct>(newPog);

  const reverseShelfRow = (row: (typeof rows)[number]) => {
    const shelves = (row.logicalShelf?.shelves ?? [])
      .map((shelf) => newPog.getShelf(shelf.uniqueId))
      .filter((shelf): shelf is NonNullable<typeof shelf> => shelf !== null);
    const reversedItems = shelves.flatMap((shelf) => shelf.items).reverse();
    let itemIndex = 0;

    for (const shelf of shelves) {
      const itemCount = shelf.items.length;
      shelf.items = reversedItems.slice(itemIndex, itemIndex + itemCount);
      itemIndex += itemCount;
      for (const item of shelf.items) item.shelf = shelf;
    }
  };

  const reversePegboardRow = (row: (typeof rows)[number]) => {
    const pegboard = row.pegboard;
    if (!pegboard || row.holeNoTop === undefined) return;
    const placements = pegboard.placements
      .filter((placement) => placement.holes[0]?.holeNoTop === row.holeNoTop)
      .sort(
        (first, second) =>
          (first.holes[0]?.holeNoLeft ?? Number.MAX_SAFE_INTEGER) -
            (second.holes[0]?.holeNoLeft ?? Number.MAX_SAFE_INTEGER) ||
          first.uniqueId.localeCompare(second.uniqueId),
      );
    const products = placements.map((placement) => placement.pegProduct).reverse();
    placements.forEach((placement, index) => {
      const product = products[index];
      if (product) placement.pegProduct = product;
    });
  };

  for (const bay of newPog.planogram.bays) {
    const bayRows = rows.filter((row) => row.bayId === bay.uniqueId);
    let indexFromBottom = 0;

    for (const row of [...bayRows].reverse()) {
      indexFromBottom += 1;
      const shouldReverse = to === "odd" ? indexFromBottom % 2 === 1 : indexFromBottom % 2 === 0;
      if (!shouldReverse) continue;

      if (row.kind === "shelf") reverseShelfRow(row);
      else reversePegboardRow(row);
    }
  }

  return newPog;
};

export const addItemsToShelfInPog = ({
  itemIds,
  pog,
  shelfId,
}: {
  itemIds: readonly string[];
  pog: Pog;
  shelfId: string;
}) => {
  const newPog = new Pog(pog.toNonCircular());
  const newShelf = newPog.getShelf(shelfId);
  const items = resolveDraggedItems(newPog, [...itemIds]);
  if (!newShelf || items.length !== itemIds.length) {
    return null;
  }

  newShelf.addItems(items);
  return newPog;
};

export const addItemsNextToItemInPog = ({
  anchorItemId,
  itemIds,
  pog,
  side,
}: {
  anchorItemId: string;
  itemIds: readonly string[];
  pog: Pog;
  side: "left" | "right";
}) => {
  const newPog = new Pog(pog.toNonCircular());
  const anchorItem = newPog.planogram.getItem(anchorItemId);
  const items = resolveDraggedItems(newPog, [...itemIds]);
  if (!anchorItem || items.length !== itemIds.length) {
    return null;
  }

  anchorItem.rangeItems(items, side);
  return newPog;
};

export const duplicateItemsToShelfInPog = ({
  itemIds,
  pog,
  shelfId,
}: {
  itemIds: string[];
  pog: Pog;
  shelfId: string;
}) => {
  const newPog = new Pog(pog.toNonCircular());
  const newShelf = newPog.getShelf(shelfId);
  if (!newShelf) {
    return null;
  }

  const items = resolveDraggedPlanogramItems(newPog, itemIds);
  if (items.length === 0) {
    return null;
  }

  newShelf.addItems(items.map((item) => item.duplicate(newShelf)));
  return newPog;
};

export const duplicateItemsNextToItemInPog = ({
  anchorItemId,
  itemIds,
  pog,
  side,
}: {
  anchorItemId: string;
  itemIds: string[];
  pog: Pog;
  side: "left" | "right";
}) => {
  const newPog = new Pog(pog.toNonCircular());
  const newAnchorItem = newPog.planogram.getItem(anchorItemId);
  if (!newAnchorItem) {
    return null;
  }

  const items = resolveDraggedPlanogramItems(newPog, itemIds);
  if (items.length === 0) {
    return null;
  }

  newAnchorItem.rangeItems(
    items.map((item) => item.duplicate(newAnchorItem.shelf)),
    side,
  );
  return newPog;
};

export const moveSingleCdtProductNextToItemInPog = ({
  anchorItemId,
  itemId,
  pog,
  pattern,
  side,
}: {
  anchorItemId: string;
  itemId: string;
  pog: Pog;
  pattern: SnakingPattern;
  side: "left" | "right";
}) => {
  if (anchorItemId === itemId) return pog;

  const currentAnchorItem = pog.planogram.getItem(anchorItemId);
  const currentDraggedItem = pog.planogram.getItem(itemId);
  if (
    !(currentAnchorItem instanceof PlanogramItem) ||
    !(currentDraggedItem instanceof PlanogramItem)
  ) {
    return null;
  }

  if (currentAnchorItem.shelf === currentDraggedItem.shelf) {
    const shelfItems = currentAnchorItem.shelf.items;
    const anchorIndex = shelfItems.indexOf(currentAnchorItem);
    const draggedIndex = shelfItems.indexOf(currentDraggedItem);
    const isAlreadyPlaced =
      (side === "left" && draggedIndex === anchorIndex - 1) ||
      (side === "right" && draggedIndex === anchorIndex + 1);
    if (isAlreadyPlaced) return pog;
  }

  const newPog = new Pog(pog.toNonCircular());
  const anchorItem = newPog.planogram.getItem(anchorItemId);
  const draggedItem = newPog.getItem(itemId);

  if (!(anchorItem instanceof PlanogramItem) || !(draggedItem instanceof PlanogramItem)) {
    return null;
  }

  anchorItem.rangeItem(draggedItem, side);
  const recalculation = recalculateCdtPriorities(newPog, pattern);
  return recalculation.success ? recalculation.pog : null;
};

type PlanogramItemSqueezeDimension = "squeezeWidth" | "squeezeHeight" | "squeezeDepth";

interface PlanogramItemCappingUpdate {
  xCapNum?: number;
  xCapOrientation?: PlanogramItemOrientation;
  yCapNum?: number;
  yCapOrientation?: PlanogramItemOrientation;
  zCapNum?: number;
  zCapOrientation?: PlanogramItemOrientation;
}

const updatePlanogramItemsInPog = ({
  itemIds,
  pog,
  update,
}: {
  itemIds: readonly string[];
  pog: Pog;
  update: (item: PlanogramItem) => void;
}) => {
  if (itemIds.length === 0) {
    return pog;
  }

  const newPog = new Pog(pog.toNonCircular());

  for (const itemId of itemIds) {
    const item = newPog.planogram.getItem(itemId);
    if (!item) {
      return null;
    }
    update(item);
  }

  return newPog;
};

export const setPlanogramItemsHorizontalFacingsInPog = ({
  horizontalFacings,
  itemIds,
  pog,
}: {
  horizontalFacings: number;
  itemIds: readonly string[];
  pog: Pog;
}) =>
  updatePlanogramItemsInPog({
    itemIds,
    pog,
    update: (item) => {
      item.facings = horizontalFacings * item.facingsRows;
    },
  });

export const setPlanogramItemsSqueezeDimensionInPog = ({
  dimension,
  isSqueezed,
  itemIds,
  pog,
}: {
  dimension: PlanogramItemSqueezeDimension;
  isSqueezed: boolean;
  itemIds: readonly string[];
  pog: Pog;
}) =>
  updatePlanogramItemsInPog({
    itemIds,
    pog,
    update: (item) => {
      item[dimension] = isSqueezed;
    },
  });

export const setPlanogramItemsMerchandisingStyleInPog = ({
  itemIds,
  merchandisingStyle,
  pog,
}: {
  itemIds: readonly string[];
  merchandisingStyle: PlanogramItemCommonNonCircular["merchandisingStyle"];
  pog: Pog;
}) =>
  updatePlanogramItemsInPog({
    itemIds,
    pog,
    update: (item) => {
      item.merchandisingStyle = merchandisingStyle;
      delete item.overhangAsUnit;
    },
  });

export const setPlanogramItemsCappingInPog = ({
  capping,
  itemIds,
  pog,
}: {
  capping: PlanogramItemCappingUpdate;
  itemIds: readonly string[];
  pog: Pog;
}) =>
  updatePlanogramItemsInPog({
    itemIds,
    pog,
    update: (item) => {
      if (capping.xCapNum !== undefined) item.xCapNum = capping.xCapNum;
      if (capping.xCapOrientation !== undefined) item.xCapOrientation = capping.xCapOrientation;
      if (capping.yCapNum !== undefined) item.yCapNum = capping.yCapNum;
      if (capping.yCapOrientation !== undefined) item.yCapOrientation = capping.yCapOrientation;
      if (capping.zCapNum !== undefined) item.zCapNum = capping.zCapNum;
      if (capping.zCapOrientation !== undefined) item.zCapOrientation = capping.zCapOrientation;
    },
  });

export const setPlanogramItemOrientationInPog = ({
  orientation,
  pog,
  planogramItems,
}: {
  orientation: PlanogramItemOrientation;
  pog: Pog;
  planogramItems: PlanogramItem[];
}) => {
  return updatePlanogramItemsInPog({
    itemIds: planogramItems.map((item) => item.uniqueId),
    pog,
    update: (item) => {
      item.orientation = orientation;
    },
  });
};

interface SetShelfWidthInPogParams {
  pog: Pog;
  shelfId: string;
  width: number;
}

export const setShelfWidthInPog = ({ pog, shelfId, width }: SetShelfWidthInPogParams) => {
  const newPog = new Pog(pog.toNonCircular());
  const newShelf = newPog.getShelf(shelfId);

  if (!newShelf) {
    return null;
  }

  const merchandisableWidth = resizeShelfWidth(newShelf, width);
  newShelf.deadSpans = resizeShelfDeadSpans(newShelf.deadSpans, width);
  newShelf.width = width;
  newShelf.merchandisableWidth = merchandisableWidth;
  return newPog;
};

interface AddBayToSideInPogParams {
  pog: Pog;
  bayId: string;
  side: "LEFT" | "RIGHT";
}

/**
 * Put a copy of this bay beside it — an empty one, with its measurements.
 *
 * A break in the run stays on the seam it was on. Added to the LEFT, the
 * original keeps its break, which is still at the right-hand end of the pair.
 * Added to the RIGHT, the break moves onto the copy, for the same reason. So
 * a run that broke once still breaks once.
 */
export const addBayToSideInPog = ({ pog, bayId, side }: AddBayToSideInPogParams) => {
  const newPog = new Pog(pog.toNonCircular());
  const sourceBay = newPog.getBay(bayId);
  if (!sourceBay) return null;

  const bayIndex = newPog.planogram.bays.findIndex((bay) => bay.uniqueId === bayId);
  const copiesTheBreak = side === "RIGHT" && sourceBay.splitAfter;
  if (copiesTheBreak) sourceBay.splitAfter = false;

  newPog.planogram.addBay(
    new PlanogramBay(
      {
        uniqueId: uuid(),
        shelves: [],
        pegboards: [],
        width: sourceBay.width,
        height: sourceBay.height,
        depth: sourceBay.depth,
        baseHeight: sourceBay.baseHeight,
        notchOffset: sourceBay.notchOffset,
        notchSpacing: sourceBay.notchSpacing,
        maxNotch: sourceBay.maxNotch,
        maxProductHead: sourceBay.maxProductHead,
        maxShelfHeight: sourceBay.maxShelfHeight,
        splitAfter: copiesTheBreak,
      },
      newPog.planogram,
    ),
    bayIndex + (side === "RIGHT" ? 1 : 0),
  );

  return newPog;
};

/** True when the run breaks after some bay. A flag on the last bay breaks nothing. */
export const runHasAisleBreak = (pog: Pog) =>
  pog.planogram.bays.slice(0, -1).some((bay) => bay.splitAfter);

interface SetSplitAfterInPogParams {
  pog: Pog;
  bayId: string;
  splitAfter: boolean;
}

/**
 * Say whether the run physically breaks after this bay — a corner, a doorway,
 * the end of one wall. The break belongs to the bay before it and to nothing
 * else, so this is the only place it is set.
 *
 * A break after the LAST bay says nothing (the run ends there anyway) and is
 * refused, which keeps the pog and the drawing telling the same story.
 */
export const setSplitAfterInPog = ({ pog, bayId, splitAfter }: SetSplitAfterInPogParams) => {
  const pogData = pog.toNonCircular();
  const bays = pogData.planogram.bays;
  const bayIndex = bays.findIndex((bay) => bay.uniqueId === bayId);

  if (bayIndex === -1) return null;
  if (splitAfter && bayIndex === bays.length - 1) return null;

  bays[bayIndex].splitAfter = splitAfter;
  // Built after the flag is set, so a shelf merge across the new split is dropped.
  return new Pog(pogData);
};

interface SetShelfMerchandisableWidthInPogParams {
  pog: Pog;
  shelfId: string;
  merchandisableWidth: number;
}

// Sets an explicit merchandisableWidth on an unmerged shelf, clamped to its physical width.
// Merged groups reject this update to preserve their fully-merchandisable invariant, and so
// does a shelf whose width an obstruction sets, so the strips drawn and the width counted agree.
export const setShelfMerchandisableWidthInPog = ({
  pog,
  shelfId,
  merchandisableWidth,
}: SetShelfMerchandisableWidthInPogParams) => {
  const newPog = new Pog(pog.toNonCircular());
  const newShelf = newPog.getShelf(shelfId);

  if (
    !newShelf ||
    newShelf.mergedLeft !== null ||
    newShelf.mergedRight !== null ||
    hasObstructionSpans(newShelf)
  ) {
    return null;
  }

  newShelf.merchandisableWidth = Math.max(0, Math.min(merchandisableWidth, newShelf.width));
  return newPog;
};

interface SetShelfAlignInPogParams {
  pog: Pog;
  shelfId: string;
  align: "left" | "right" | "between" | "around";
}

export const setShelfAlignInPog = ({ pog, shelfId, align }: SetShelfAlignInPogParams) => {
  const newPog = new Pog(pog.toNonCircular());
  const newShelf = newPog.getShelf(shelfId);

  if (!newShelf) {
    return null;
  }

  newShelf.align = align;
  return newPog;
};

export const setShelvesTypeInPog = ({
  pog,
  shelfIds,
  type,
}: {
  pog: Pog;
  shelfIds: string[];
  type: PlanogramShelf["type"];
}) => {
  const newPog = new Pog(pog.toNonCircular());
  const shelves: PlanogramShelf[] = [];

  for (const shelfId of shelfIds) {
    const shelf = newPog.getShelf(shelfId);
    if (!shelf) return null;
    shelves.push(shelf);
  }

  for (const shelf of shelves) {
    for (const mergedShelf of shelf.getMergedShelves()) {
      mergedShelf.type = type;
      if (type !== "REGULAR") mergedShelf.maxFrontOverhang = 0;
    }
  }

  return newPog;
};

export const setShelvesTypeAndUnrangeItemsInPog = ({
  pog,
  shelfIds,
  type,
  productCodes,
}: {
  pog: Pog;
  shelfIds: string[];
  type: PlanogramShelf["type"];
  productCodes: string[];
}) => {
  const newPog = setShelvesTypeInPog({ pog, shelfIds, type });
  if (!newPog) return null;

  const targetShelves = new Map<string, PlanogramShelf>();
  for (const shelfId of shelfIds) {
    const shelf = newPog.getShelf(shelfId);
    if (!shelf) return null;
    for (const mergedShelf of shelf.getMergedShelves()) {
      targetShelves.set(mergedShelf.uniqueId, mergedShelf);
    }
  }

  for (const productCode of new Set(productCodes)) {
    const shelvesWithProduct = [...targetShelves.values()].filter((shelf) =>
      shelf.items.some((item) => item.productCode === productCode),
    );
    const item = shelvesWithProduct[0]?.items.find(
      (shelfItem) => shelfItem.productCode === productCode,
    );
    if (!item) continue;

    const potentialShelves = shelvesWithProduct.map((shelf) => shelf.uniqueId);
    const existingUnrangedItem = newPog.unrangedItems.find(
      (unrangedItem) => unrangedItem.productCode === productCode,
    );

    if (existingUnrangedItem) {
      existingUnrangedItem.potentialShelves = [
        ...new Set([...existingUnrangedItem.potentialShelves, ...potentialShelves]),
      ];
    } else {
      newPog.unrangedItems.unshift(
        new UnrangedPlanogramItem(
          {
            ...adapterPlanogramItemToPlanogramItemCommon(item),
            potentialShelves,
            reasons: [],
          },
          newPog,
        ),
      );
    }

    for (const shelf of shelvesWithProduct) {
      shelf.items = shelf.items.filter((shelfItem) => shelfItem.productCode !== productCode);
    }
  }

  return newPog;
};

// bay.shelves is kept sorted by descending notch (highest notch first, see
// PlanogramBay.fixShelvesOrder), so the next-lower-notch neighbor is at index + 1 and the
// next-higher-notch neighbor is at index - 1.
export const canBeMovedDown = (shelf: PlanogramShelf) => {
  const index = shelf.bay.shelves.findIndex((s) => s.uniqueId === shelf.uniqueId);
  const bottomShelf: PlanogramShelf | undefined = shelf.bay.shelves[index + 1];

  if (bottomShelf) {
    return shelf.getNotchNo() > bottomShelf.getNotchNo() + 1;
  }

  return shelf.getNotchNo() > 1;
};

export const canBeMovedUp = (shelf: PlanogramShelf) => {
  const index = shelf.bay.shelves.findIndex((s) => s.uniqueId === shelf.uniqueId);
  const topShelf: PlanogramShelf | undefined = shelf.bay.shelves[index - 1];

  if (topShelf) {
    return topShelf.getNotchNo() > shelf.getNotchNo() + 1;
  }

  return shelf.getNotchNo() < shelf.bay.maxNotch;
};

// How many notches above the original a duplicated shelf (and its merged run) is placed.
const DUPLICATE_SHELF_NOTCH_OFFSET = 3;

const getDuplicateShelfTargetNotch = (shelf: PlanogramShelf) =>
  Math.min(shelf.getNotchNo() + DUPLICATE_SHELF_NOTCH_OFFSET, shelf.bay.maxNotch);

export const duplicateShelvesInPog = ({ pog, shelfIds }: { pog: Pog; shelfIds: string[] }) => {
  if (shelfIds.length === 0) {
    return null;
  }

  const newPog = new Pog(pog.toNonCircular());
  const selectedShelves = shelfIds.flatMap((shelfId) => {
    const shelf = newPog.getShelf(shelfId);
    return shelf ? [shelf] : [];
  });

  if (selectedShelves.length !== shelfIds.length) {
    return null;
  }

  const shelfGroups = new Map<string, PlanogramShelf>();
  for (const shelf of selectedShelves) {
    const firstShelf = shelf.getFirstShelf();
    shelfGroups.set(firstShelf.uniqueId, firstShelf);
  }

  for (const firstShelf of shelfGroups.values()) {
    const mergedShelves = firstShelf.getMergedShelves();
    const duplicatedShelves = mergedShelves.map((shelf) => {
      const targetNotch = getDuplicateShelfTargetNotch(shelf);

      return {
        sourceShelf: shelf,
        duplicatedShelf: new PlanogramShelf(
          {
            uniqueId: getNewOwaShelfId(),
            notchNo: targetNotch,
            mergedLeft: null,
            mergedRight: null,
            items: [],
            width: shelf.width,
            merchandisableWidth: shelf.merchandisableWidth,
            deadSpans: shelf.deadSpans,
            dividers: shelf.dividers,
            loaderDepth: shelf.loaderDepth,
            depth: shelf.depth,
            thickness: shelf.thickness,
            offset: shelf.offset,
            grillHeight: shelf.grillHeight,
            type: shelf.type,
            variantRestrictions: [...shelf.variantRestrictions],
            align: shelf.align,
            assembly: shelf.assembly,
            standsOn: standsOnWithinBay(shelf.standsOn, shelf.bay),
          },
          shelf.bay,
        ),
      };
    });

    const isBlocked = duplicatedShelves.some(({ sourceShelf, duplicatedShelf }) =>
      sourceShelf.bay.shelves.some(
        (candidate) => candidate.getNotchNo() === duplicatedShelf.getNotchNo(),
      ),
    );

    if (isBlocked) {
      return null;
    }

    const firstDuplicatedShelf = duplicatedShelves[0].duplicatedShelf;
    const duplicatesCanBeMerged = duplicatedShelves.every(
      ({ duplicatedShelf }) =>
        duplicatedShelf.getYBottom() === firstDuplicatedShelf.getYBottom() &&
        duplicatedShelf.thickness === firstDuplicatedShelf.thickness,
    );

    if (!duplicatesCanBeMerged) {
      return null;
    }

    let previousDuplicate: PlanogramShelf | null = null;
    for (const { sourceShelf, duplicatedShelf } of duplicatedShelves) {
      duplicatedShelf.items = sourceShelf.items.map((item) => item.duplicate(duplicatedShelf));

      if (previousDuplicate) {
        previousDuplicate.mergedRight = duplicatedShelf;
        duplicatedShelf.mergedLeft = previousDuplicate;
      }
      previousDuplicate = duplicatedShelf;

      sourceShelf.bay.addShelf(duplicatedShelf);
    }
  }

  return newPog;
};

export const moveShelvesInPog = ({
  pog,
  shelfIds,
  notchOffset,
}: {
  pog: Pog;
  shelfIds: readonly string[];
  notchOffset: number;
}) => {
  if (shelfIds.length === 0 || notchOffset === 0) {
    return pog;
  }

  const newPog = new Pog(pog.toNonCircular());
  const selectedShelves = shelfIds.flatMap((shelfId) => {
    const shelf = newPog.getShelf(shelfId);
    return shelf ? [shelf] : [];
  });

  if (selectedShelves.length !== shelfIds.length) {
    return null;
  }

  const shelfGroups = new Map<string, PlanogramShelf>();
  for (const shelf of selectedShelves) {
    const firstShelf = shelf.getFirstShelf();
    shelfGroups.set(firstShelf.uniqueId, firstShelf);
  }

  const movingShelfIds = new Set(
    [...shelfGroups.values()].flatMap((shelf) =>
      shelf.getMergedShelves().map((mergedShelf) => mergedShelf.uniqueId),
    ),
  );

  for (const shelfId of movingShelfIds) {
    const shelf = newPog.getShelf(shelfId);
    if (!shelf) {
      return null;
    }

    const targetNotch = shelf.getNotchNo() + notchOffset;
    const isOutsideBay = targetNotch < 1 || targetNotch > shelf.bay.maxNotch;
    const collidesWithStationaryShelf = shelf.bay.shelves.some(
      (candidate) =>
        !movingShelfIds.has(candidate.uniqueId) && candidate.getNotchNo() === targetNotch,
    );

    if (isOutsideBay || collidesWithStationaryShelf) {
      return null;
    }
  }

  for (const shelf of shelfGroups.values()) {
    shelf.setNotchNo(shelf.getNotchNo() + notchOffset);
  }

  return newPog;
};

/**
 * Moves a shelf to another bay. A rack stands in its host's bay: a moved rack
 * board leaves its host behind, and a moved host leaves its rack boards
 * behind, so a `standsOn` across bays is dropped on both sides.
 */
export const moveShelfToBayNoInPog = ({
  pog,
  shelfId,
  bayNo,
}: {
  pog: Pog;
  shelfId: string;
  bayNo: number;
}) => {
  const newPog = new Pog(pog.toNonCircular());
  const shelf = newPog.getShelf(shelfId);
  if (!shelf) return null;

  const fromBay = shelf.bay;
  shelf.moveShelfToBayNo(bayNo);
  for (const bay of new Set([fromBay, shelf.bay])) {
    for (const board of bay.shelves) {
      board.standsOn = standsOnWithinBay(board.standsOn, bay);
    }
  }
  return newPog;
};

interface PogActionsOptions {
  onApplySnaking?: () => void;
}

export const usePogAtomActions = (
  pogAtom: PrimitiveAtom<Pog | null>,
  options: PogActionsOptions = {},
) => {
  const [pog, setPog] = useAtom(pogAtom);
  return usePogActions({ pog, setPog, ...options });
};

export const usePogActions = ({
  pog,
  setPog: setPogUnchecked,
  onApplySnaking,
}: {
  pog: Pog | null;
  setPog: React.Dispatch<React.SetStateAction<Pog | null>>;
  onApplySnaking?: () => void;
}) => {
  const { toast } = useToasts();
  const { t } = useTranslation("common");

  // Every edit is fit-checked: a row, orientation, cap or shelf change that
  // leaves a stack taller than the room above its shelf is flagged, not blocked.
  const setPog: typeof setPogUnchecked = (update) =>
    setPogUnchecked((prev) => {
      const next = typeof update === "function" ? update(prev) : update;
      if (prev) warnStacksOverClearance(findNewStacksOverClearance(prev, next));
      if (next) {
        const previous = new Set(prev ? frontOverhangViolations(prev).map((v) => v.message) : []);
        const violations = frontOverhangViolations(next).filter((v) => !previous.has(v.message));
        if (violations.length)
          toast({
            title: t("frontOverhang.needsCorrection"),
            message: violations.map((v) => frontOverhangViolationText(next, v)).join("\n"),
            type: "WARNING",
          });
      }
      return next;
    });

  const warnStacksOverClearance = (stacks: StackOverClearance[]) => {
    if (stacks.length === 0) return;
    const describe = ({ item, maxRows }: StackOverClearance) =>
      t(maxRows === 1 ? "planogramFitCheck.rowFits" : "planogramFitCheck.rowsFit", {
        item: item.name,
        count: maxRows,
      });
    toast({
      title:
        stacks.length === 1
          ? t("planogramFitCheck.singleStackTitle")
          : t("planogramFitCheck.multipleStacksTitle", { count: stacks.length }),
      message:
        stacks.slice(0, 3).map(describe).join("; ") +
        (stacks.length > 3 ? `; ${t("planogramFitCheck.more", { count: stacks.length - 3 })}` : ""),
      type: "WARNING",
    });
  };
  const setSnakingInferenceRequest = useSetAtom(cdtSnakingInferenceRequestAtom);

  // An aisle break belongs to the store's fixtures. Anywhere else, a run that carries one keeps
  // its bays, so no edit can move, drop or copy the break.
  const canDeclareAisleBreaks = useCanDeclareAisleBreaks();
  const keepsItsBays = (pog: Pog) => {
    if (canDeclareAisleBreaks || !runHasAisleBreak(pog)) return false;
    toast({ title: t("planogramCanvas.bayMenu.baysKeptByAisleBreak"), type: "ERROR" });
    return true;
  };

  /**
   * False when the run keeps its bays (see `keepsItsBays`), so a caller can
   * hold on to its selection: the bay is still there.
   */
  const deleteBay = (bay: PlanogramBay): boolean => {
    if (!pog || keepsItsBays(pog)) return false;
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      const newBay = newPog.getBay(bay.uniqueId);
      if (newBay) {
        newBay.delete();
      } else {
        toast({
          title: "Failed to delete bay",
          type: "ERROR",
        });
      }
      return newPog;
    });
    return true;
  };

  const addBayToSide = (bay: PlanogramBay, side: "LEFT" | "RIGHT") => {
    setPog((pog) => {
      if (!pog || keepsItsBays(pog)) return pog;
      const newPog = addBayToSideInPog({ bayId: bay.uniqueId, pog, side });
      if (!newPog) {
        toast({
          title: "Failed to add bay",
          type: "ERROR",
        });
        return pog;
      }
      return newPog;
    });
  };

  // Only the seam handle on a store's fixtures calls this, and it never offers the last bay,
  // so a refusal (unknown bay, break after the last bay) leaves the pog as it was.
  const setSplitAfter = (bayId: string, splitAfter: boolean) => {
    setPog((pog) => {
      if (!pog) return pog;
      return setSplitAfterInPog({ bayId, pog, splitAfter }) ?? pog;
    });
  };

  const moveShelf = (shelf: PlanogramShelf, notchNo: number) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      const newShelf = newPog.getShelf(shelf.uniqueId);
      if (newShelf) {
        newShelf.setNotchNo(notchNo);
      } else {
        toast({
          title: "Failed to set shelf notch number",
          type: "ERROR",
        });
      }
      return newPog;
    });
  };

  const deleteShelf = (shelf: PlanogramShelf) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      const newShelf = newPog.getShelf(shelf.uniqueId);
      if (newShelf) {
        newShelf.delete();
      } else {
        toast({
          title: "Failed to delete shelf",
          type: "ERROR",
        });
      }
      return newPog;
    });
  };

  const duplicateShelves = (shelves: PlanogramShelf[]) => {
    setPog((pog) => {
      if (!pog) return pog;

      const newPog = duplicateShelvesInPog({
        pog,
        shelfIds: shelves.map((shelf) => shelf.uniqueId),
      });
      if (!newPog) {
        toast({
          title: "Failed to duplicate shelf",
          type: "ERROR",
        });
        return pog;
      }

      return newPog;
    });
  };

  const mergeLeft = (shelf: PlanogramShelf) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      const newShelf = newPog.getShelf(shelf.uniqueId);
      if (newShelf) {
        newShelf.mergeLeft();
      } else {
        toast({
          title: "Failed to merge shelf left",
          type: "ERROR",
        });
      }
      return newPog;
    });
  };

  const unmergeLeft = (shelf: PlanogramShelf) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      const newShelf = newPog.getShelf(shelf.uniqueId);
      if (newShelf) {
        newShelf.unmergeLeft();
      } else {
        toast({
          title: "Failed to unmerge shelf left",
          type: "ERROR",
        });
      }
      return newPog;
    });
  };

  const mergeRight = (shelf: PlanogramShelf) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      const newShelf = newPog.getShelf(shelf.uniqueId);
      if (newShelf) {
        newShelf.mergeRight();
      } else {
        toast({
          title: "Failed to merge shelf right",
          type: "ERROR",
        });
      }
      return newPog;
    });
  };

  const unmergeRight = (shelf: PlanogramShelf) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      const newShelf = newPog.getShelf(shelf.uniqueId);
      if (newShelf) {
        newShelf.unmergeRight();
      } else {
        toast({
          title: "Failed to unmerge shelf right",
          type: "ERROR",
        });
      }
      return newPog;
    });
  };

  const moveShelves = (shelfIds: string[], notchOffset: number) => {
    setPog((pog) => {
      if (!pog) return pog;

      const newPog = moveShelvesInPog({ pog, shelfIds, notchOffset });
      if (!newPog) {
        toast({
          title: "Failed to move shelves",
          type: "ERROR",
        });
        return pog;
      }

      return newPog;
    });
  };

  const deleteShelves = (shelves: PlanogramShelf[]) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      for (const shelf of shelves) {
        const newShelf = newPog.getShelf(shelf.uniqueId);
        if (newShelf) {
          newShelf.delete();
        } else {
          toast({
            title: "Failed to delete shelf",
            type: "ERROR",
          });
        }
      }
      return newPog;
    });
  };

  const sortShelvesItemsBy = (shelves: PlanogramShelf[], sortBy: keyof PlanogramItem) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      for (const shelf of shelves) {
        const newShelf = newPog.getShelf(shelf.uniqueId);
        if (newShelf) {
          newShelf.sortItemsBy(sortBy);
        } else {
          toast({
            title: "Failed to sort shelf items",
            type: "ERROR",
          });
        }
      }
      return newPog;
    });
  };

  const mergeShelvesLeft = (shelves: PlanogramShelf[]) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      for (const shelf of shelves) {
        const newShelf = newPog.getShelf(shelf.uniqueId);
        if (newShelf) {
          newShelf.mergeLeft();
        } else {
          toast({
            title: "Failed to merge shelf left",
            type: "ERROR",
          });
        }
      }
      return newPog;
    });
  };

  const unmergeShelvesLeft = (shelves: PlanogramShelf[]) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      for (const shelf of shelves) {
        const newShelf = newPog.getShelf(shelf.uniqueId);
        if (newShelf) {
          newShelf.unmergeLeft();
        } else {
          toast({
            title: "Failed to unmerge shelf left",
            type: "ERROR",
          });
        }
      }
      return newPog;
    });
  };

  const mergeShelvesRight = (shelves: PlanogramShelf[]) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      for (const shelf of shelves) {
        const newShelf = newPog.getShelf(shelf.uniqueId);
        if (newShelf) {
          newShelf.mergeRight();
        } else {
          toast({
            title: "Failed to merge shelf right",
            type: "ERROR",
          });
        }
      }
      return newPog;
    });
  };

  const unmergeShelvesRight = (shelves: PlanogramShelf[]) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      for (const shelf of shelves) {
        const newShelf = newPog.getShelf(shelf.uniqueId);
        if (newShelf) {
          newShelf.unmergeRight();
        } else {
          toast({
            title: "Failed to unmerge shelf right",
            type: "ERROR",
          });
        }
      }
      return newPog;
    });
  };

  const setShelfMaxFrontOverhang = (shelf: PlanogramShelf, allowance: number) => {
    if (!Number.isFinite(allowance) || allowance < 0) return;
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      const newShelf = newPog.getShelf(shelf.uniqueId);
      if (
        !newShelf ||
        newShelf.type !== "REGULAR" ||
        newShelf.slope ||
        newShelf.mergedLeft ||
        newShelf.mergedRight
      )
        return pog;
      newShelf.maxFrontOverhang = Math.round(allowance);
      if (newShelf.maxFrontOverhang > 0) newPog.frontOverhangEnabled = true;
      return newPog;
    });
  };

  const setShelfDepth = (shelf: PlanogramShelf, depth: number) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      const newShelf = newPog.getShelf(shelf.uniqueId);
      if (newShelf) {
        newShelf.depth = depth;
      } else {
        toast({
          title: "Failed to set shelf depth",
          type: "ERROR",
        });
      }
      return newPog;
    });
  };

  const setShelfWidth = (shelf: PlanogramShelf, width: number) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = setShelfWidthInPog({
        pog,
        shelfId: shelf.uniqueId,
        width,
      });

      if (!newPog) {
        toast({
          title: "Failed to set shelf width",
          type: "ERROR",
        });
        return pog;
      }
      return newPog;
    });
  };

  const setShelfMerchandisableWidth = (shelf: PlanogramShelf, merchandisableWidth: number) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = setShelfMerchandisableWidthInPog({
        pog,
        shelfId: shelf.uniqueId,
        merchandisableWidth,
      });

      if (!newPog) {
        toast({
          title: "Failed to set shelf merchandisable width",
          type: "ERROR",
        });
        return pog;
      }
      return newPog;
    });
  };

  const setShelfAlign = (shelf: PlanogramShelf, align: "left" | "right" | "between" | "around") => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = setShelfAlignInPog({
        pog,
        shelfId: shelf.uniqueId,
        align,
      });

      if (!newPog) {
        toast({
          title: "Failed to set shelf alignment",
          type: "ERROR",
        });
        return pog;
      }
      return newPog;
    });
  };

  const setShelfOffset = (shelf: PlanogramShelf, offset: number) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      const newShelf = newPog.getShelf(shelf.uniqueId);
      if (newShelf) {
        newShelf.offset = offset;
      } else {
        toast({
          title: "Failed to set shelf offset",
          type: "ERROR",
        });
      }
      return newPog;
    });
  };

  const setShelfThickness = (shelf: PlanogramShelf, thickness: number) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      const newShelf = newPog.getShelf(shelf.uniqueId);
      if (newShelf) {
        newShelf.thickness = thickness;
      } else {
        toast({
          title: "Failed to set shelf thickness",
          type: "ERROR",
        });
      }
      return newPog;
    });
  };

  const setShelvesType = (shelves: PlanogramShelf[], type: PlanogramShelf["type"]) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = setShelvesTypeInPog({
        pog,
        shelfIds: shelves.map((shelf) => shelf.uniqueId),
        type,
      });
      if (!newPog) {
        toast({
          title: t("shelfTypes.setFailed"),
          type: "ERROR",
        });
        return pog;
      }
      return newPog;
    });
  };

  const setShelvesTypeAndUnrangeItems = (
    shelves: PlanogramShelf[],
    type: PlanogramShelf["type"],
    productCodes: string[],
  ) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = setShelvesTypeAndUnrangeItemsInPog({
        pog,
        shelfIds: shelves.map((shelf) => shelf.uniqueId),
        type,
        productCodes,
      });
      if (!newPog) {
        toast({
          title: t("shelfTypes.setFailed"),
          type: "ERROR",
        });
        return pog;
      }
      return newPog;
    });
  };

  const setShelfNotchNo = (shelf: PlanogramShelf, notchNo: number) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      const newShelf = newPog.getShelf(shelf.uniqueId);
      if (newShelf) {
        newShelf.setNotchNo(notchNo);
      } else {
        toast({
          title: "Failed to set shelf notch number",
          type: "ERROR",
        });
      }
      return newPog;
    });
  };

  const moveShelfToBayNo = (shelf: PlanogramShelf, bayNo: number) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = moveShelfToBayNoInPog({ pog, shelfId: shelf.uniqueId, bayNo });
      if (!newPog) {
        toast({
          title: "Failed to move shelf to bay",
          type: "ERROR",
        });
        return pog;
      }
      return newPog;
    });
  };

  const unrangeItems = (items: PlanogramItem[]) => {
    setPog((pog) => {
      if (!pog) return pog;

      const newPog = new Pog(pog.toNonCircular());

      for (const item of items) {
        const newItem = newPog.planogram.getItem(item.uniqueId);
        if (newItem) {
          newItem.unrange();
        } else {
          toast({
            title: `Failed to unrange item ${item.productCode}`,
            type: "ERROR",
          });
        }
      }

      return newPog;
    });
  };

  const addItemsToShelf = (
    items: (PlanogramItem | UnrangedPlanogramItem)[],
    shelf: PlanogramShelf,
  ) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = addItemsToShelfInPog({
        itemIds: items.map((item) => item.uniqueId),
        pog,
        shelfId: shelf.uniqueId,
      });
      if (!newPog) {
        toast({
          title: "Failed to add item to shelf",
          type: "ERROR",
        });
        return pog;
      }
      return newPog;
    });
  };

  const duplicateItemsToShelf = (itemIds: string[], shelf: PlanogramShelf) => {
    setPog((pog) => {
      if (!pog) return pog;

      const newPog = duplicateItemsToShelfInPog({
        itemIds,
        pog,
        shelfId: shelf.uniqueId,
      });
      if (!newPog) {
        toast({
          title: "Failed to duplicate items to shelf",
          type: "ERROR",
        });
        return pog;
      }

      return newPog;
    });
  };

  const deleteItem = (item: PlanogramItem | UnrangedPlanogramItem, storeCodes: string[]) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      const newItem = newPog.getItem(item.uniqueId);
      if (newItem) {
        newItem.delete(storeCodes);
      } else {
        toast({
          title: "Failed to delete item",
          type: "ERROR",
        });
      }
      return newPog;
    });
  };

  const addItemNextToAnotherItem = (
    anchorItem: PlanogramItem,
    droppedItems: (PlanogramItem | UnrangedPlanogramItem)[],
    side: "left" | "right",
  ) => {
    setPog((pog) => {
      if (!pog) return null;
      const newPog = addItemsNextToItemInPog({
        anchorItemId: anchorItem.uniqueId,
        itemIds: droppedItems.map((item) => item.uniqueId),
        pog,
        side,
      });
      if (!newPog) {
        toast({
          title: "Failed to add item next to another item",
          type: "ERROR",
        });
        return pog;
      }
      return newPog;
    });
  };

  const duplicateItemsNextToItem = (
    anchorItem: PlanogramItem,
    itemIds: string[],
    side: "left" | "right",
  ) => {
    setPog((pog) => {
      if (!pog) return pog;

      const newPog = duplicateItemsNextToItemInPog({
        anchorItemId: anchorItem.uniqueId,
        itemIds,
        pog,
        side,
      });
      if (!newPog) {
        toast({
          title: "Failed to duplicate items next to another item",
          type: "ERROR",
        });
        return pog;
      }

      return newPog;
    });
  };

  const addNewShelf = (bayNo: number, notchNo?: number) => {
    if (!pog) return null;
    const newPog = new Pog(pog.toNonCircular());
    const newShelfId = getNewOwaShelfId();
    const newBay = newPog.planogram.bays[bayNo - 1];
    if (!newBay) {
      toast({
        title: "Failed to add new shelf",
        type: "ERROR",
      });
      return null;
    }

    const thickness = newBay.shelves[0]?.thickness || 100;
    const newShelf = Object.assign(
      new PlanogramShelf(
        {
          uniqueId: newShelfId,
          mergedLeft: null,
          mergedRight: null,
          items: [],
          width: newBay.width,
          merchandisableWidth: newBay.width,
          depth: newBay.depth,
          thickness,
          offset: 0,
          notchNo: notchNo || firstNotchOnDeck(newBay, thickness),
          grillHeight: 0,
          type: "REGULAR",
          variantRestrictions: [],
        },
        newBay,
      ),
      { isNew: true },
    );
    newBay.addShelf(newShelf);

    setPog((pog) => {
      if (!pog) return pog;
      return newPog;
    });

    return newShelf;
  };

  const sortShelfItemsBy = (shelf: PlanogramShelf, sortBy: keyof PlanogramItem) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      const newShelf = newPog.getShelf(shelf.uniqueId);
      if (newShelf) {
        newShelf.sortItemsBy(sortBy);
      } else {
        toast({
          title: "Failed to sort shelf items",
          type: "ERROR",
        });
      }
      return newPog;
    });
  };

  const unrangeContainer = (container: PlanogramContainer) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      const newContainer = newPog.containers.find((c) => c.uniqueId === container.uniqueId);
      if (newContainer) {
        newContainer.unrange();
      } else {
        toast({
          title: "Failed to unrange container",
          type: "ERROR",
        });
      }
      return newPog;
    });
  };

  const deleteContainer = (container: PlanogramContainer) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      const newContainer = newPog.containers.find((c) => c.uniqueId === container.uniqueId);
      if (newContainer) {
        newContainer.delete();
      } else {
        toast({
          title: "Failed to delete container",
          type: "ERROR",
        });
      }
      return newPog;
    });
  };

  const splitContainerRight = (container: PlanogramContainer) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      const newContainer = newPog.containers.find((c) => c.uniqueId === container.uniqueId);
      if (newContainer) {
        newContainer.splitRight();
      } else {
        toast({
          title: "Failed to split container right",
          type: "ERROR",
        });
      }
      return newPog;
    });
  };

  const splitContainerBottom = (container: PlanogramContainer) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      const newContainer = newPog.containers.find((c) => c.uniqueId === container.uniqueId);
      if (newContainer) {
        newContainer.splitBottom();
      } else {
        toast({
          title: "Failed to split container bottom",
          type: "ERROR",
        });
      }
      return newPog;
    });
  };

  const restoreContainer = (container: UnrangedPlanogramContainer) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());
      const newContainer = newPog.unrangedContainers.find((c) => c.uniqueId === container.uniqueId);
      if (newContainer) {
        newContainer.restore();
      } else {
        toast({
          title: "Failed to restore container",
          type: "ERROR",
        });
      }
      return newPog;
    });
  };

  const setHorizontalFacings = (planogramItems: PlanogramItem[], horizontalFacings: number) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = setPlanogramItemsHorizontalFacingsInPog({
        horizontalFacings,
        itemIds: planogramItems.map((item) => item.uniqueId),
        pog,
      });
      if (!newPog) {
        toast({
          title: "Failed to set horizontal facings",
          type: "ERROR",
        });
        return pog;
      }
      return newPog;
    });
  };

  const setVerticalFacings = (planogramItems: PlanogramItem[], verticalFacings: number) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = new Pog(pog.toNonCircular());

      for (const planogramItem of planogramItems) {
        const item = newPog.planogram.getItem(planogramItem.uniqueId);
        if (item) {
          const horizontalFacings = item.facings / item.facingsRows;
          item.facingsRows = verticalFacings;
          item.facings = horizontalFacings * item.facingsRows;
        } else {
          toast({
            title: `Failed to set item ${planogramItem.uniqueId} horizontal facings`,
            type: "ERROR",
          });
        }
      }

      return newPog;
    });
  };

  const setOrientation = (
    planogramItems: PlanogramItem[],
    orientation: PlanogramItemOrientation,
  ) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = setPlanogramItemOrientationInPog({
        orientation,
        pog,
        planogramItems,
      });

      if (!newPog) {
        toast({
          title: "Failed to set orientation",
          type: "ERROR",
        });
        return pog;
      }

      if (!canSetPlanogramItemOrientation(planogramItems, orientation)) {
        toast({
          title: "Selected orientation is not supported by all selected items",
          type: "WARNING",
        });
      }

      return newPog;
    });
  };

  const setCapping = (planogramItems: PlanogramItem[], capping: PlanogramItemCappingUpdate) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = setPlanogramItemsCappingInPog({
        capping,
        itemIds: planogramItems.map((item) => item.uniqueId),
        pog,
      });
      if (!newPog) {
        toast({
          title: "Failed to update capping",
          type: "ERROR",
        });
        return pog;
      }
      return newPog;
    });
  };

  const setSqueezeDimension = (
    planogramItems: PlanogramItem[],
    dimension: PlanogramItemSqueezeDimension,
    isSqueezed: boolean,
  ) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = setPlanogramItemsSqueezeDimensionInPog({
        dimension,
        isSqueezed,
        itemIds: planogramItems.map((item) => item.uniqueId),
        pog,
      });
      if (!newPog) {
        toast({
          title: "Failed to update squeeze dimension",
          type: "ERROR",
        });
        return pog;
      }
      return newPog;
    });
  };

  const setMerchandisingStyle = (
    planogramItems: PlanogramItem[],
    merchandisingStyle: PlanogramItemCommonNonCircular["merchandisingStyle"],
  ) => {
    setPog((pog) => {
      if (!pog) return pog;
      const newPog = setPlanogramItemsMerchandisingStyleInPog({
        itemIds: planogramItems.map((item) => item.uniqueId),
        merchandisingStyle,
        pog,
      });
      if (!newPog) {
        toast({
          title: "Failed to update merchandising style",
          type: "ERROR",
        });
        return pog;
      }
      return newPog;
    });
  };

  const flipShelvesHorizontally = (shelves: PlanogramShelf[]) => {
    setPog((pog) => {
      if (!pog) return pog;

      const newPog = flipShelvesHorizontallyInPog({
        pog,
        shelfIds: shelves.map((shelf) => shelf.uniqueId),
      });

      return newPog ?? pog;
    });
  };

  const flipItemsHorizontally = (itemIds: string[]) => {
    setPog((pog) => {
      if (!pog) return pog;

      return flipItemsHorizontallyInPog({ pog, itemIds }) ?? pog;
    });
  };

  const applySnaking = (to: "odd" | "even") => {
    if (!pog) return;

    const newPog = applySnakingInPog({ pog, to });
    onApplySnaking?.();
    setPog(newPog);
    setSnakingInferenceRequest({ pog: newPog.toNonCircular() });
  };

  const saveChanges = () => {
    setPog((pog) => {
      if (!pog) return pog;
      return new Pog(pog.toNonCircular());
    });
  };

  return {
    addItemNextToAnotherItem,
    addItemsToShelf,
    duplicateItemsNextToItem,
    duplicateItemsToShelf,
    addNewShelf,
    addBayToSide,
    applySnaking,
    flipShelvesHorizontally,
    flipItemsHorizontally,
    canBeMovedDown,
    canBeMovedUp,
    deleteContainer,
    deleteItem,
    deleteBay,
    deleteShelf,
    deleteShelves,
    duplicateShelves,
    mergeLeft,
    mergeShelvesLeft,
    mergeRight,
    mergeShelvesRight,
    moveShelf,
    moveShelves,
    moveShelfToBayNo,
    pog,
    restoreContainer,
    saveChanges,
    setPog,
    setHorizontalFacings,
    setShelfDepth,
    setShelfMaxFrontOverhang,
    setShelfWidth,
    setShelfMerchandisableWidth,
    setShelfAlign,
    setShelfNotchNo,
    setShelfOffset,
    setSplitAfter,
    setShelfThickness,
    setShelvesType,
    setShelvesTypeAndUnrangeItems,
    setOrientation,
    setCapping,
    setMerchandisingStyle,
    setVerticalFacings,
    setSqueezeDimension,
    sortShelfItemsBy,
    sortShelvesItemsBy,
    splitContainerBottom,
    splitContainerRight,
    unmergeLeft,
    unmergeShelvesLeft,
    unmergeRight,
    unmergeShelvesRight,
    unrangeContainer,
    unrangeItems,
  };
};

export const getNewOwaShelfId = () => `owa-${uuid()}`;
